/**
 * Features Content
 * Strongly-typed content for the Features page
 * All feature descriptions, FAQs, and messaging centralized here
 */

export interface Feature {
  id: string;
  title: string;
  description: string;
  icon: string; // SVG path or emoji
}

export interface FeatureCategory {
  id: string;
  title: string;
  description: string;
  features: string[]; // Bullet points
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
}

export interface TrustItem {
  id: string;
  text: string;
  icon: string;
}

// Hero content
export const HERO_CONTENT = {
  badge: {
    text: 'Draft-only • Clinician must review',
    icon: '⚕️',
  },
  title: 'Features built for safe, compliant clinical documentation',
  subtitle:
    'Notematic generates draft clinical notes across 7 structured formats — IAR, SOAP, DAP, BIRP, GIRP, PIRP and PIE — to support your workflow. All drafts require clinician review and approval. You maintain full control over consent, content, and export decisions.',
  primaryCTA: {
    text: 'Book Demo',
    href: '/contact',
  },
  secondaryCTA: {
    text: 'View Privacy Notice',
    href: '/privacy',
  },
} as const;

// Main feature grid (8 cards)
export const FEATURE_GRID: Feature[] = [
  {
    id: 'secure-capture',
    title: 'Secure Session Capture',
    description:
      'Secure session capture with Azure-grade security. Audio is processed in-memory only and never stored persistently. All other data stored in Australian data centres with strict access controls.',
    icon: '🎙️',
  },
  {
    id: 'consent-management',
    title: 'All-Party Consent Management',
    description:
      'All session participants must consent across four scopes—Recording, Transcription, Draft Generation, and Export—before any session begins. Consent gates all downstream actions and is retained for 7 years.',
    icon: '✓',
  },
  {
    id: 'draft-notes',
    title: 'AI-Generated Draft Clinical Notes — 7 Formats',
    description:
      'AI generates draft notes in 7 structured clinical formats — IAR (Initial Assessment & Referral), SOAP, DAP, BIRP, GIRP, PIRP and PIE — as a starting point only. All drafts are clearly marked as AI-assisted and require clinician review. AI never makes diagnoses, prescribes treatment, or classifies risk.',
    icon: '📝',
  },
  {
    id: 'clinician-review',
    title: 'Clinician Review & Safety Controls',
    description:
      'Mandatory human-in-the-loop review for all AI outputs. High-risk indicators (suicidality, abuse, imminent harm) are flagged for immediate clinician attention. No draft is finalised without your explicit approval.',
    icon: '👨‍⚕️',
  },
  {
    id: 'consent-gated-export',
    title: 'Consent-Gated Export to Your EMR/CRM',
    description:
      'Export approved notes in six formats—PDF (official watermarked record), Word (editable working copy), JSON (structured data for integration), ZIP Bundle (complete record with PDF, JSON, and metadata manifest), Markdown, or plain text (clipboard with provenance footer). Export is only permitted when all consent requirements are met and the clinician has approved the content. Your EMR/CRM remains the system of record.',
    icon: '🔒',
  },
  {
    id: 'audit-trail',
    title: 'Immutable Audit Trail',
    description:
      'Every action is logged with timestamp, user, and context. Audit logs are stored in tamper-proof immutable storage and retained for 7 years for compliance and regulatory audits.',
    icon: '📋',
  },
  {
    id: 'retention-deletion',
    title: 'Automated Retention & Deletion',
    description:
      'Draft notes deleted within 24 hours after export to your EMR/CRM. Transcripts deleted with their associated draft. Audio is never stored — processed in-memory only and discarded immediately after transcription. Consent records and audit logs retained for 7 years.',
    icon: '🗂️',
  },
  {
    id: 'clinical-refiner',
    title: 'Clinical Refiner — AI-Assisted Note Polish',
    description:
      'Transform clinician shorthand, abbreviations, and rough draft notes into professional, AHPRA-aligned clinical documentation. Works section-by-section with Replace mode (rewrite from scratch) or Augment mode (merge new input into existing content). All refinements are previewed before acceptance — the clinician always has the final word.',
    icon: '✨',
  },
  {
    id: 'voice-refiner',
    title: 'Voice-to-Note Refiner — Dictate, Review, Approve',
    description:
      'Dictate clinical observations directly into any note section. Audio is transcribed, then refined into structured clinical language — all in one step. A three-panel comparison shows the raw dictation, the AI-refined version, and existing content so you can verify every word before accepting. Included with monthly voice minutes per plan (Practice: 100 min, Centre: 250 min).',
    icon: '🎙️',
  },
  {
    id: 'referral-directory',
    title: 'Referral Service Directory',
    description:
      'Maintain a searchable directory of services, practitioners, and programmes your clinicians can refer clients to. Layered visibility (Organisation, Regional, Global) lets you share entries across your network. Import services from a URL or CSV, and a 90-day verification cycle keeps entries current — stale services are flagged automatically.',
    icon: '📋',
  },
  {
    id: 'document-templates',
    title: 'Document Template Management & Generation',
    description:
      'Create and manage governed templates for referral letters, GP correspondence, and admin notices. Templates follow a Draft → Approved → Archived lifecycle with full version history. Placeholder tokens (e.g. {{ClientName}}) are merged at generation time — the generated document exists only in browser memory and is never persisted to the database.',
    icon: '📄',
  },
  {
    id: 'desktop-pwa',
    title: 'Installable Desktop App (PWA)',
    description:
      'Install Notematic directly to your clinic computer or tablet as a secure Progressive Web App. Enjoy a native app recording experience without requiring complex IT provisioning or local administrator rights.',
    icon: '💻',
  },
  {
    id: 'azure-security',
    title: 'Azure-native Security & Monitoring',
    description:
      'Built on Azure with encryption at rest and in transit, managed identities, Key Vault integration, and continuous security monitoring.',
    icon: '🛡️',
  },
] as const;

// Deep dive categories — "How It Works" section
export const FEATURE_CATEGORIES: FeatureCategory[] = [
  {
    id: 'clinical-workflow',
    title: 'Clinical Workflow',
    description: 'Designed to support, not replace, clinical practice',
    features: [
      'All-party consent captured across four scopes (Recording, Transcription, Draft, Export) before any session begins',
      'Draft clinical notes generated in 7 structured formats (IAR, SOAP, DAP, BIRP, GIRP, PIRP, PIE) as a starting point—clinician reviews, edits, and approves all content before finalisation',
      'Approved notes exported to your clinic\'s EMR/CRM, which remains the authoritative system of record',
      'Notematic holds temporary working copies only—drafts are deleted within 24 hours after export verification',
      'Referral service directory with layered visibility (Organisation/Regional/Global), CSV and URL import, and 90-day verification lifecycle',
      'Document template library with governed lifecycle (Draft → Approved → Archived), placeholder merging, and version history — generated documents are volatile, never persisted',
    ],
  },
  {
    id: 'safety-compliance',
    title: 'Safety & Compliance',
    description: 'Built for Australian privacy and healthcare regulations',
    features: [
      'Australian Privacy Principles (APP) compliance with data minimisation—Notematic is a processing tool, not a record-keeping system',
      'Audio is never stored — processed in-memory only and discarded immediately after transcription. Transcripts deleted with their associated draft; drafts deleted within 24 hours after export.',
      'Consent records retained for 7 years per Health Records Act 2001; audit logs retained for 7 years in immutable storage',
      'Two-stage deletion process (soft-delete with recovery window, then permanent hard-delete) with full audit trail',
    ],
  },
  {
    id: 'intelligence-quality',
    title: 'Intelligence & Quality',
    description: 'AI assistance with mandatory clinical oversight',
    features: [
      'Human-in-the-loop principle: AI never makes final clinical decisions—all outputs require explicit clinician review and approval',
      'AI does not diagnose, prescribe treatment, classify risk, or make legal or clinical determinations',
      'High-risk indicators (suicidality, abuse, psychosis, imminent harm) flagged for immediate clinician review—no automated interpretation',
      'All AI-generated content clearly marked as draft—no autonomous submission to permanent clinical records',
      'Clinical Refiner transforms shorthand and abbreviations into professional, AHPRA-aligned language—section by section with clinician preview and approval',
      'Voice-to-Note Refiner lets clinicians dictate observations directly into note sections, with raw dictation visible for review before accepting the refined version',
    ],
  },
  {
    id: 'platform-integration',
    title: 'Platform & Integration',
    description: 'Secure, scalable, and integration-ready',
    features: [
      'Azure-native architecture with encryption at rest and in transit, managed identities, and Key Vault—all data in Australian data centres',
      'Export to six formats—PDF, Word (DOCX), JSON, ZIP Bundle (complete record), Markdown, and plain text—for integration with your EMR/CRM system of record',
      'API-ready design for future integration with clinical management platforms',
    ],
  },
  {
    id: 'phn-management',
    title: 'PHN & Network Management',
    description: 'Centralised governance for commissioned programs',
    features: [
      'Network-wide Dashboard provides real-time visibility into rollout, adoption, and session counts across all commissioned sites',
      'Automated Site Provisioning instantly spins up new, fully-isolated clinical sites with pre-allocated seats and voice minutes',
      'Centralised Team Management allows secure onboarding of fellow PHN administrators with mandatory MFA enforcement',
      'Tenant-scoped architecture ensures strict data separation and privacy boundaries between participating sites',
      'Aggregated audit logging provides oversight without exposing sensitive patient health information',
    ],
  },
  {
    id: 'operations-tools',
    title: 'Operations & Practice Tools',
    description: 'Manage referrals and correspondence efficiently',
    features: [
      'Searchable referral service directory with categories, cost types, specialisations, age-group filters, and eligibility criteria',
      'Layered visibility lets organisations share referral entries regionally or globally across a PHN network',
      'Import referral services from a URL (AI-assisted extraction) or CSV bulk upload',
      '90-day verification cycle flags stale entries — clinicians confirm services are still active with a single click',
      'Governed document templates with placeholder tokens ({{ClientName}}, {{GPName}}, etc.) resolved at generation time',
      'Generated documents exist only in browser memory — PII is never persisted to the database',
      'Full version history and status governance (Draft → Approved → Archived) for template compliance',
    ],
  },
] as const;

// Trust strip items
export const TRUST_ITEMS: TrustItem[] = [
  {
    id: 'consent',
    text: 'All-party consent, 4 scopes',
    icon: '✓',
  },
  {
    id: 'draft-only',
    text: 'Draft-only, clinician-approved',
    icon: '⚕️',
  },
  {
    id: 'processing-tool',
    text: 'Processing tool, not a system of record',
    icon: '🔄',
  },
  {
    id: 'encryption',
    text: 'Australian data centres, encrypted',
    icon: '🔒',
  },
  {
    id: 'retention',
    text: 'Auto-delete after export',
    icon: '🗂️',
  },
] as const;

// FAQ items
export const FAQ_ITEMS: FAQ[] = [
  {
    id: 'clinical-judgement',
    question: 'Does Notematic replace clinical judgement?',
    answer:
      'No. Notematic generates draft notes only. All drafts must be reviewed, edited, and approved by the clinician before export. The AI does not diagnose, prescribe treatment, classify risk, or make clinical determinations. High-risk indicators such as suicidality or imminent harm are flagged for immediate clinician review. You maintain full clinical responsibility and decision-making authority.',
  },
  {
    id: 'consent-handling',
    question: 'How is consent handled?',
    answer:
      'Notematic requires all-party consent before any session begins. Every participant must consent across four scopes: Recording, Transcription, Draft Generation, and Export. Consent status gates all downstream actions—you cannot export or share notes unless all required consents are valid. Consent records are retained for 7 years per the Health Records Act 2001. Participants can revoke consent, triggering automated deletion workflows.',
  },
  {
    id: 'data-retention',
    question: 'What data do you retain and for how long?',
    answer:
      'Audio recordings: not retained — processed in-memory only and discarded immediately after transcription. Draft notes: deleted within 24 hours after export to your EMR/CRM. Transcripts: deleted with their associated draft. Consent records: 7 years. Audit logs: 7 years in immutable storage. Notematic holds temporary working copies only — your EMR/CRM is the system of record.',
  },
  {
    id: 'system-of-record',
    question: 'Is Notematic a system of record?',
    answer:
      'No. Notematic is a clinical documentation processing tool, not a record-keeping system. It temporarily processes audio, transcripts, and drafts to support note generation. Once you export an approved note to your clinic\'s EMR/CRM, Notematic deletes the working data within 24 hours. Your EMR/CRM remains the authoritative repository for all clinical records and statutory retention obligations.',
  },
  {
    id: 'export-integration',
    question: 'Can I export to my practice system?',
    answer:
      'Yes. Approved notes can be exported in six formats: PDF (watermarked official record), Word/DOCX (editable working copy marked "Not Authoritative"), JSON (structured data bundle), ZIP Bundle (complete record containing the official PDF, canonical JSON, and a metadata manifest), Markdown, or plain text (clipboard copy with provenance footer). Export is only permitted when all consent requirements are met and the clinician has approved the content. You are responsible for verifying successful export—Notematic will delete drafts within 24 hours after export.',
  },
  {
    id: 'clinical-refiner',
    question: 'What is the Clinical Refiner?',
    answer:
      'The Clinical Refiner is an AI-assisted tool that transforms clinician shorthand, abbreviations, and rough notes into professional, AHPRA-aligned clinical documentation. It works section-by-section with two modes: Replace (rewrite the section from your input) and Augment (merge new input into existing content). All refinements are previewed in a side-by-side comparison — you review and approve before anything changes. Text refinement is included in all plans at no additional cost.',
  },
  {
    id: 'voice-refiner',
    question: 'What is the Voice-to-Note Refiner and are there usage limits?',
    answer:
      'The Voice-to-Note Refiner lets you dictate clinical observations directly into any note section. Your dictation is transcribed and then refined into structured clinical language. A three-panel comparison shows the raw dictation, the refined version, and any existing content so you can verify every word. Voice dictation is included with monthly minute allowances: Practice plan (100 min/month), Centre plan (250 min/month), Network/PHN (negotiated). Text refinement remains unlimited on all plans.',
  },
  {
    id: 'referral-directory',
    question: 'How does the Referral Service Directory work?',
    answer:
      'The directory lets you maintain a searchable library of services, practitioners, and programmes for client referrals. You can add entries manually, import from a URL (AI-assisted extraction), or bulk-upload via CSV. Each entry includes contact details, specialisations, age groups, cost type, eligibility criteria, and referral method. A 90-day verification cycle automatically flags stale entries so your team can confirm services are still active. Visibility layers (Organisation, Regional, Global) allow sharing across your PHN network.',
  },
  {
    id: 'document-templates',
    question: 'What are Document Templates and how is privacy protected?',
    answer:
      'Document Templates let admins create governed templates for referral letters, GP correspondence, and admin notices. Templates use placeholder tokens (e.g. {{ClientName}}, {{GPName}}) that are resolved at generation time. The generated document exists only in browser memory — it is never saved to the Notematic database. You copy or download the result; Notematic retains no trace. Templates follow a governed lifecycle (Draft → Approved → Archived) with full version history for compliance.',
  },
] as const;

// Footer CTA content
export const FOOTER_CTA_CONTENT = {
  title: 'Ready to see Notematic in your service?',
  subtitle: 'Book a demo or request a guided pilot for your centre, service, or network.',
  disclaimer: 'Privacy-first enquiry handling. No spam.',
  placeholder: 'Work email',
  buttonText: 'Request Demo',
} as const;

